/**
 * The registry as a list of *commands*, which is not the same list.
 *
 * An action is a verb with a parameter — "change what the picture-in-picture
 * shows" takes a view. A palette row is a whole thought: **"Show peers"**. Listing
 * the actions raw gives a palette where half the rows, when picked, refuse with
 * "name one of the panel's views" — which is a menu that asks a question it just
 * offered to answer.
 *
 * So commands are **derived** from the registry rather than written beside it.
 * A second hand-maintained list is the thing the registry exists to prevent, and
 * it would go stale the first time an enum gained a value.
 *
 * ## Three shapes, and one of them is deliberately left out
 *
 * | the action's required args | what it becomes |
 * |---|---|
 * | none | one command |
 * | exactly one, with an `enum` | one command per value |
 * | anything else | **nothing** |
 *
 * The third is `setPipFrequency`, whose interval is an open pattern rather than
 * a fixed set: `2d`, `45mi` and `3m` are all legal, so there is no list to
 * expand into. It stays reachable from the panel's own bar and from the agent,
 * which is where a typed value belongs. A palette row that opened a second
 * prompt would be a different feature, and pretending the value is an enum would
 * offer four intervals out of an infinity of them.
 *
 * ## A row that asks a second question, in the same box
 *
 * An action with a `palette` entry gets one row ending in `…` that does not
 * run: it switches the box into a second step. `browse` is a sub-search over
 * the action's `choices` (Add indicator: MACD, Paste into: Watchlist); `text`
 * is a text box whose Enter calls the action's `ask` (Describe a column:). That
 * is the "different feature" the paragraph above set aside — built once, here,
 * rather than as a prompt per action, and still derived from the registry.
 *
 * ## Rows from what is typed
 *
 * Two actions mark themselves `typed`: their argument is neither a set nor a
 * second question but the query itself. `avgo` is **Go to AVGO** (the symbol
 * index, ranked as `SymbolDialog` ranks it) and `1w` is **Interval — 1 week**
 * (the toolbar's parser). `topCommands` puts those rows around the ordinary
 * matches, and **Ask AI** last — a palette-only row, never a registry action,
 * so the agent cannot call it.
 */

import type { Frequency } from "@qufolio/model";

import { ACTIONS, matchScore, type Action, type ActionContext, type ActionOutcome } from "./actions";
import {
  activeToken,
  canonicalSymbol,
  committedSymbol,
  isComposite,
  parseComposite,
  replaceActiveToken,
} from "./composite";
import { parseGroupPhrase } from "./group-phrase";
import { contentWords, searchGuide } from "./guide-index";
import { parseIndicatorPhrase, phraseArgs } from "./indicator-phrase";
import { intervalName } from "./interval-label";
import { INTERVALS, canonicalInterval, composeInterval } from "./interval-mru";
import { AI_REVIEW_LABEL, FIN_ST_LABEL, type PipView } from "./pip";
import { firstOutside } from "./symbol-access";
import { symbolResolver } from "./symbol-index";
import { isSymbolLocked } from "./symbol-lock";
import { rankSymbols } from "./symbol-rank";

export interface Command {
  /** Stable across renders, so a list can key on it. */
  key: string;
  action: Action;
  /** The arguments this row means, already filled in. */
  args: Record<string, unknown>;
  /** The whole thought, as a row reads it. */
  title: string;
  /** Words this row answers to beyond its action's — set for a data-driven choice. */
  keywords?: readonly string[];
  /** The row's subtitle when it is not the action's description — `hint`, or a choice's detail. */
  subtitle?: string;
  /**
   * Picking this row opens a second step in the same box rather than running:
   * a sub-search over the action's choices, or a text box (`PaletteEntry`).
   */
  enter?: "browse" | "text";
  /** One value of a data-driven argument — kept off an empty box. */
  choice?: boolean;
}

/** The required properties of an action's schema, and their enums if any. */
function requiredEnums(action: Action): { name: string; values: string[] }[] | null {
  const schema = action.inputSchema as {
    required?: unknown;
    properties?: Record<string, { enum?: unknown }>;
  };
  const required = Array.isArray(schema.required) ? (schema.required as string[]) : [];
  if (required.length === 0) return [];
  if (required.length > 1) return null;
  const name = required[0]!;
  const values = schema.properties?.[name]?.enum;
  if (!Array.isArray(values)) return null;
  return [{ name, values: values.filter((v): v is string => typeof v === "string") }];
}

/**
 * How a filled-in argument reads on the row.
 *
 * `Show picture-in-picture — peers` rather than a sentence rebuilt per action:
 * the action's own title already says the verb, and re-phrasing it per value
 * would be a table of English that has to be maintained beside the registry —
 * exactly the second list this module refuses to keep.
 */
function commandTitle(action: Action, value?: string): string {
  return value === undefined ? action.title : `${action.title} — ${value}`;
}

/**
 * Every action, expanded into the commands a palette can offer.
 *
 * With a `ctx`, an action that declares `choices` expands into one row per
 * choice — the indicator catalog, which is data rather than an enum. Without
 * one those actions have no rows, exactly as before.
 */
export function paletteCommands(actions: readonly Action[] = ACTIONS, ctx?: ActionContext): Command[] {
  const out: Command[] = [];
  for (const action of actions) {
    // The brand masks: an action this context does not offer has no row at all,
    // rather than a row that refuses (CLAUDE.md, Branding).
    if (ctx && action.available && !action.available(ctx)) continue;
    // Built from the query by `topCommands`, never listed up front.
    if (action.typed) continue;
    const subtitle = ctx && action.hint ? { subtitle: action.hint(ctx) } : {};
    if (action.palette) {
      // Every second step reads the context — the catalog, the lists, the plan.
      if (!ctx) continue;
      const entry = action.palette;
      const browse = entry.mode === "browse" && (entry.browseWhen?.(ctx) ?? true);
      out.push({
        key: `${action.id}…`,
        action,
        args: {},
        title: entry.title,
        ...subtitle,
        // A browse row whose `browseWhen` says no runs at once, with no args:
        // Paste symbols… with a list on screen files into it.
        ...(entry.mode === "text" ? { enter: "text" as const } : browse ? { enter: "browse" as const } : {}),
      });
      if (entry.mode === "browse" && !entry.nested) {
        out.push(...choiceRows(action, ctx).map((c) => ({ ...c, title: commandTitle(action, c.title) })));
      }
      continue;
    }
    if (action.choices) {
      if (!ctx) continue;
      out.push(...choiceRows(action, ctx).map((c) => ({ ...c, title: commandTitle(action, c.title) })));
      continue;
    }
    const required = requiredEnums(action);
    // Needs a typed value the palette has nowhere to put. Left out rather than
    // listed-and-refusing, which is the failure this whole module is avoiding.
    if (required === null) continue;
    if (required.length === 0) {
      const title = ctx && action.label ? action.label(ctx) : commandTitle(action);
      out.push({ key: action.id, action, args: {}, title, ...subtitle });
      continue;
    }
    const { name, values } = required[0]!;
    for (const value of values) {
      out.push({
        key: `${action.id}:${value}`,
        action,
        args: { [name]: value },
        title: commandTitle(action, value),
      });
    }
  }
  return out;
}

/** The argument a data-driven choice fills: the required one, else the first that is not the chart. */
function choiceArg(action: Action): string | undefined {
  const schema = action.inputSchema as { required?: string[]; properties?: Record<string, unknown> };
  return schema.required?.[0] ?? Object.keys(schema.properties ?? {}).find((k) => k !== "chartGroupId");
}

/** One row per choice, titled by the choice alone — a sub-search's rows. */
function choiceRows(action: Action, ctx: ActionContext): Command[] {
  const name = choiceArg(action);
  if (!name || !action.choices) return [];
  return action.choices(ctx).map((choice) => ({
    key: `${action.id}:${choice.value}`,
    action,
    args: { [name]: choice.value },
    title: choice.label,
    ...(choice.detail ? { subtitle: choice.detail } : {}),
    keywords: choice.keywords ?? [],
    choice: true,
  }));
}

/**
 * The rows of a browse step — `Add indicator:` and its catalog, `Paste into:`
 * and the user's lists. Titled by the choice alone: the box's prefix already
 * says the verb, and `Add indicator — MACD` under `Add indicator:` says it twice.
 */
export function browseCommands(action: Action, ctx: ActionContext): Command[] {
  return choiceRows(action, ctx);
}

/**
 * What a browse step lists for a query: its choices, matched — and under
 * `Add indicator:`, a typed phrase's readings above them (`atr 5 sma 20`).
 */
export function browseHits(query: string, action: Action, choices: readonly Command[], ctx: ActionContext): Command[] {
  const phrases = action.id === "addIndicator" ? indicatorPhraseCommands(query, ctx, true) : [];
  return [...phrases, ...searchCommands(query, choices)];
}

/**
 * What an empty box lists: every command but the per-choice rows. Two hundred
 * "Add indicator — …" rows would bury the dozen everything else is; the
 * `Add indicator…` row is the way to them, and typing `rsi` still finds one.
 */
export function emptyQueryCommands(commands: readonly Command[]): Command[] {
  return commands.filter((c) => !c.choice);
}

/**
 * The commands a typed query matches, best first.
 *
 * Scored through `matchScore`, the same function the raw action list uses, so
 * the palette and the agent's own view of the registry cannot rank the same
 * words differently. The **command's** title is what is matched, so `peers`
 * finds the row that shows peers rather than every row of the action it belongs
 * to; the action's keywords and description come along, which is how `pop out`
 * finds detach and `monitor` finds it too.
 */
export function searchCommands(query: string, commands: readonly Command[]): Command[] {
  const q = query.trim();
  if (!q) return [...commands];
  const scored: { command: Command; score: number }[] = [];
  for (const command of commands) {
    const score = matchScore(q, {
      title: command.title,
      keywords: command.keywords ? [...command.keywords, ...command.action.keywords] : command.action.keywords,
      description: command.action.description,
    });
    if (score > 0) scored.push({ command, score });
  }
  // Stable within a score: the registry's order is the order these were designed
  // in, and re-sorting equal hits alphabetically would scatter the PIP commands
  // through the list.
  return scored.sort((a, b) => b.score - a.score).map((s) => s.command);
}

// ── rows from what is typed ─────────────────────────────────────────────────

/** How many Go to rows a query offers — the head of the ranking, not a list to scroll. */
export const SYMBOL_ROWS = 5;

/**
 * Whether a query could be a ticker or a ratio of them — no spaces inside an
 * operand, operators allowed between. `add a 21 sma` is not; `avgo / spy` is.
 */
const TICKERISH = /^[A-Za-z0-9.&^]+(?:\s*[+\-*/]\s*[A-Za-z0-9.&^]*)*$/;

/** The benchmark the compare row ratios a US ticker against (owner's mock). */
const BENCHMARK = "SPY";

/** Where Go to will land, said before it does: a locked active chart falls through. */
function goToHint(ctx: ActionContext): string {
  const active = ctx.activeChartGroupId ? ctx.chartGroup(ctx.activeChartGroupId) : undefined;
  return isSymbolLocked(active) ? "active chart is locked — the next unlocked chart" : "on the active chart";
}

/**
 * Go to rows for a query: the symbol index ranked by `rankSymbols` — exactly
 * `SymbolDialog`'s ranking, so an exact ticker is first — at most
 * `SYMBOL_ROWS` of them. An exact US ticker is followed by its compare row,
 * `AVGO/SPY`. A ratio ranks the operand being typed, as the dialog's active
 * token does, and offers the whole expression in the commit's spelling.
 *
 * `exact` says the query named a ticker (or a ratio) outright, which is what
 * puts these rows above the commands.
 */
export function symbolCommands(
  query: string,
  ctx: ActionContext,
  actions: readonly Action[] = ACTIONS,
): { rows: Command[]; exact: boolean } {
  const none = { rows: [], exact: false };
  const action = actions.find((a) => a.id === "goToSymbol");
  if (!action || (action.available && !action.available(ctx))) return none;
  const text = query.trim();
  if (!text || text.length > 40 || !TICKERISH.test(text)) return none;
  const row = (symbol: string, title: string, subtitle: string): Command => ({
    key: `goToSymbol:${symbol}`,
    action,
    args: { symbol },
    title,
    subtitle,
  });

  // No symbol service: nothing to rank and no plan to check, so what was typed
  // is offered as typed — `SymbolDialog`'s commit on the same build.
  if (!ctx.symbols) {
    const symbol = committedSymbol(text);
    return { rows: [row(symbol, `Go to ${symbol}`, goToHint(ctx))], exact: true };
  }
  const index = ctx.symbols();
  if (!index) return none;
  const holds = symbolResolver(index)!;

  if (isComposite(text, holds)) {
    const token = activeToken(text, text.length);
    const needle = token.value.trim();
    if (!needle) return none;
    const rows: Command[] = [];
    for (const hit of rankSymbols(index, needle, SYMBOL_ROWS)) {
      const symbol = committedSymbol(replaceActiveToken(text, text.length, hit.symbol).text);
      // Every leg in the plan, or no row: the dialog would refuse it on Enter.
      if (!parseComposite(symbol) || firstOutside(symbol, holds) !== null) continue;
      rows.push(row(symbol, `Go to ${symbol} — compare`, "ratio chart"));
    }
    return { rows, exact: rows.length > 0 };
  }

  const ranked = rankSymbols(index, text, SYMBOL_ROWS);
  const exact = ranked[0]?.symbol.toLowerCase() === text.toLowerCase();
  const rows: Command[] = [];
  ranked.forEach((hit, i) => {
    rows.push(row(hit.symbol, hit.name ? `Go to ${hit.symbol} — ${hit.name}` : `Go to ${hit.symbol}`, goToHint(ctx)));
    if (i === 0 && exact && (hit.market ?? "US") === "US" && hit.symbol !== BENCHMARK && holds(BENCHMARK)) {
      const ratio = `${canonicalSymbol(hit.symbol)}/${BENCHMARK}`;
      rows.push(row(ratio, `Go to ${ratio} — compare`, "ratio chart"));
    }
  });
  return { rows, exact };
}

/**
 * What a typed interval means, through the toolbar's own rules.
 *
 * `canonicalInterval` is the input rule — any casing of the stored vocabulary,
 * `mi` minute and `m` month. **A bare lower-case `m` has two readings**: the
 * vocabulary's month, and the minute every chart button writes as `15m`
 * (`interval-label.ts`). Rather than pick one silently — the trap `mi` exists
 * to remove — both rows are offered, minute first, each named in words. `M` is
 * a month only and `mi` a minute only.
 */
export function intervalReadings(text: string): Frequency[] {
  const compact = text.replace(/\s+/g, "");
  const canonical = canonicalInterval(compact);
  if (!canonical) return [];
  const bare = /^([1-9][0-9]*)m$/.exec(compact);
  const minute = bare ? composeInterval(Number(bare[1]), "mi") : null;
  return minute ? [minute, canonical] : [canonical];
}

/**
 * Interval rows: a typed interval (`2d`, `45m`, `1M`, `interval 1w`), or the
 * toolbar menu's presets (`INTERVALS`) for `interval` / `timeframe` typed.
 */
export function intervalCommands(
  query: string,
  ctx: ActionContext,
  actions: readonly Action[] = ACTIONS,
): Command[] {
  const action = actions.find((a) => a.id === "setInterval");
  if (!action || (action.available && !action.available(ctx))) return [];
  const q = query.trim().toLowerCase();
  const asked = q.replace(/^(interval|timeframe)\s+/, "");
  const presets = q.length >= 3 && ["interval", "timeframe"].some((word) => word.startsWith(q));
  const values = presets ? INTERVALS : intervalReadings(asked);
  return values.map((frequency) => ({
    key: `setInterval:${frequency}`,
    action,
    args: { interval: frequency },
    title: `Interval — ${intervalName(frequency)}`,
    subtitle: "active chart",
  }));
}

/**
 * Add indicator rows from a typed phrase — `atr 5`, `macd 5 35 5`,
 * `atr 5 sma 20` (`lib/indicator-phrase.ts`, owner, 2026-10-18). One row per
 * reading, the rule's first; each runs the whole reading as one `addIndicator`
 * call, so one dispatch and one undo. `nested` titles them for the
 * `Add indicator:` step, where the prefix already says the verb.
 *
 * A locked family still gets its row, and the pick refuses with the picker's
 * reason — the catalog rows' rule.
 */
export function indicatorPhraseCommands(
  query: string,
  ctx: ActionContext,
  nested = false,
  actions: readonly Action[] = ACTIONS,
): Command[] {
  const action = actions.find((a) => a.id === "addIndicator");
  if (!action || (action.available && !action.available(ctx))) return [];
  return parseIndicatorPhrase(query, ctx.indicators()).map((reading) => ({
    key: `addIndicator=${reading.key}`,
    action,
    args: phraseArgs(reading.items),
    title: nested ? reading.title : commandTitle(action, reading.title),
    subtitle: reading.where,
  }));
}

/**
 * **Ask AI** — palette-only, and deliberately not in `ACTIONS`: `clientTools`
 * never sees it, so the agent cannot call "ask the agent". Opens the chat with
 * the typed text as the first message (`ctx.askAi`).
 */
export const ASK_AI: Action = {
  id: "askAi",
  title: "Ask AI",
  description: "Open the chat with what was typed as the first message.",
  keywords: [],
  inputSchema: { type: "object", properties: { text: { type: "string" } }, additionalProperties: false },
  available: (ctx) => ctx.askAi?.() !== undefined,
  run: (ctx, args): ActionOutcome => {
    const open = ctx.askAi?.();
    if (!open) return { ok: false, reason: "the chat is not included in your plan" };
    const text = typeof args["text"] === "string" ? args["text"].trim() : "";
    if (!text) return { ok: false, reason: "type what to ask" };
    open(text);
    return { ok: true };
  },
};

// ── rows for a question, and for groups ─────────────────────────────────────

/**
 * The matches for a typed **question** — "where can I see fundamentals". The
 * ordinary search compares the whole text, so a question matched nothing at all;
 * this drops the filler (`contentWords`) and keeps a command that answers every
 * word that is left. Only used when the whole text matched nothing.
 */
export function searchCommandsByWords(query: string, commands: readonly Command[]): Command[] {
  const words = contentWords(query);
  if (words.length === 0) return [];
  const scored: { command: Command; score: number }[] = [];
  for (const command of commands) {
    if (command.choice) continue;
    const item = {
      title: command.title,
      keywords: command.keywords ? [...command.keywords, ...command.action.keywords] : command.action.keywords,
      description: command.action.description,
    };
    let score = 0;
    for (const word of words) {
      const s = matchScore(word, item);
      if (s === 0) {
        score = 0;
        break;
      }
      score += s;
    }
    if (score > 0) scored.push({ command, score });
  }
  return scored.sort((a, b) => b.score - a.score).map((s) => s.command);
}

/**
 * The data panel's tabs by what people call them, for "Open the data panel —
 * Fin St" rows. The tab's own name is first, so the row prints it; the rest are
 * the words someone asking for it types.
 */
const DATA_TAB_WORDS: readonly { tab: PipView; label: string; words: readonly string[] }[] = [
  {
    tab: "fundamentals",
    label: FIN_ST_LABEL,
    words: ["fin st", "fundamental", "financial", "statement", "earning", "revenue", "eps", "margin", "income"],
  },
  { tab: "summary", label: AI_REVIEW_LABEL, words: ["ai review", "review", "summary", "analyst", "note"] },
  { tab: "peers", label: "Peers", words: ["peer", "competitor", "rival"] },
  { tab: "news", label: "News", words: ["news", "headline"] },
  { tab: "profile", label: "Profile", words: ["profile", "company", "business", "about"] },
];

/** "fundamentals" → Open the data panel — Fin St. One row per tab a word names. */
export function dataTabCommands(query: string, ctx: ActionContext, actions: readonly Action[] = ACTIONS): Command[] {
  const action = actions.find((a) => a.id === "openDataPanel");
  if (!action || (action.available && !action.available(ctx))) return [];
  const text = ` ${contentWords(query).join(" ")} `;
  if (text.trim() === "") return [];
  return DATA_TAB_WORDS.filter(({ words }) =>
    words.some((w) => (w.includes(" ") ? text.includes(` ${w} `) : new RegExp(` ${w}s? `).test(text))),
  ).map(({ tab, label }) => ({
    key: `openDataPanel:${tab}`,
    action,
    args: { tab },
    title: `${action.title} — ${label}`,
    subtitle: "on the active chart",
  }));
}

/** How many Guide rows a query offers. */
export const GUIDE_ROWS = 3;

/** Guide › Screening on fundamentals — the guide's own sections, opened at the one picked. */
export function guideCommands(query: string, ctx: ActionContext, actions: readonly Action[] = ACTIONS): Command[] {
  const action = actions.find((a) => a.id === "openGuide");
  if (!action || (action.available && !action.available(ctx))) return [];
  const entries = ctx.guide?.();
  if (!entries) return [];
  return searchGuide(entries, query, GUIDE_ROWS).map((entry) => ({
    key: `openGuide:${entry.anchor}`,
    action,
    args: { anchor: entry.anchor },
    title: `Guide › ${entry.heading}`,
    subtitle: entry.section === entry.heading ? "user guide" : `user guide · ${entry.section}`,
  }));
}

/**
 * "select group sp500 and russell 2000" (owner, 2026-10-21): with two or more
 * groups, **either** (union) first, then **both** (intersect), then the ◎ list
 * to pick by hand; "or" means either, so it drops the intersect row. One group
 * is one row, and the list.
 */
export function groupCommands(query: string, ctx: ActionContext, actions: readonly Action[] = ACTIONS): Command[] {
  const action = actions.find((a) => a.id === "selectGroups");
  if (!action || (action.available && !action.available(ctx))) return [];
  const all = ctx.screenerCommands?.()?.groups?.();
  if (!all) return [];
  const phrase = parseGroupPhrase(query, all);
  if (!phrase) return [];
  const codes = phrase.groups.map((g) => g.code);
  const names = phrase.groups.map((g) => g.name).join(" + ");
  const how = phrase.add ? "added to the groups picked now" : "in place of the groups picked now";
  const row = (combine: "or" | "and", title: string): Command => ({
    key: `selectGroups:${combine}:${phrase.add ? "+" : ""}${codes.join(",")}`,
    action,
    args: { groups: codes, combine, ...(phrase.add ? { add: true } : {}) },
    title,
    subtitle: `screener ◎ · ${how}`,
  });
  const rows =
    phrase.groups.length === 1
      ? [row("or", `${phrase.add ? "Add group" : "Select group"} — ${names}`)]
      : [
          row("or", `${action.title} — ${names} — either (union)`),
          ...(phrase.joiner === "or" ? [] : [row("and", `${action.title} — ${names} — both (intersect)`)]),
        ];
  const list = actions.find((a) => a.id === "openGroupPicker");
  if (list && (!list.available || list.available(ctx))) {
    rows.push({ key: "openGroupPicker", action: list, args: {}, title: `${list.title}…`, subtitle: "tick groups by hand" });
  }
  return rows;
}

/**
 * The top level's rows for a typed query, in the owner's order: a parsed
 * interval; a typed indicator phrase (`atr 5 sma 20`); Go to rows when the query names a ticker (or nothing else
 * matched by title); the ordinary matches; Go to rows otherwise; and **Ask AI
 * always last** where the chat is offered.
 */
export function topCommands(query: string, all: readonly Command[], ctx: ActionContext): Command[] {
  const text = query.trim();
  if (!text) return emptyQueryCommands(all);
  const groups = groupCommands(text, ctx);
  const whole = searchCommands(text, all);
  // A question matches nothing as a whole; its subject still may.
  const matched = whole.length > 0 ? whole : searchCommandsByWords(text, all);
  const intervals = intervalCommands(text, ctx);
  const phrases = indicatorPhraseCommands(text, ctx);
  const symbols = symbolCommands(text, ctx);
  const tabs = dataTabCommands(text, ctx);
  const q = text.toLowerCase();
  const titled = matched.some((c) => c.title.toLowerCase().includes(q));
  const lead = symbols.exact || !titled;
  const rows = [
    ...groups,
    ...intervals,
    ...phrases,
    ...(lead ? symbols.rows : []),
    ...tabs,
    ...matched,
    ...(lead ? [] : symbols.rows),
    // The guide's answers after the app's own: a command does it, the guide says where.
    ...guideCommands(text, ctx),
  ];
  if (ASK_AI.available!(ctx)) {
    rows.push({ key: "askAi", action: ASK_AI, args: { text }, title: `Ask AI: “${text}”`, subtitle: "↵ opens chat" });
  }
  // One row per key: a preset and a parsed interval can be the same command.
  const seen = new Set<string>();
  return rows.filter((c) => (seen.has(c.key) ? false : (seen.add(c.key), true)));
}
